import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { CropBox, PhotoUploadVm } from './types'

/**
 * Прикрепление фото к существующему товару (RP-FB56, PHI-13a): «Для товара «…» установить это фото?» — миниатюра нового снимка и «Сохранить».
 * Снимок остаётся ВРЕМЕННОЙ загрузкой товара (поток PSR-5): главным или дополнительным его не делают, в источник он уходит кнопкой в окне фото товара.
 */
export interface PhotoAttachVm {
  product: QueryView
  /** Название товара; `null` — ещё не прочитано. */
  productName: string | null
  /** Адрес снимка на устройстве; `null` — кадра нет. */
  newUrl: string | null
  /** Кадр был, но потерян (страницу обновили). */
  frameLost: boolean
  upload: PhotoUploadVm | null
  canWrite: boolean
  /** Можно ли нажать «Сохранить» и почему нет. */
  blocker: string | null
}

export interface PhotoAttachActions {
  save: () => void
  cancel: () => void
  cancelUpload: () => void
  retryUpload: () => void
  dismissUpload: () => void
  /** «Принять» на проверке обрезки (RP-FB73): сохранить фото по этой рамке (в пикселях выреза). */
  acceptCrop: (box: CropBox) => void
  reload: () => void
}

export interface PhotoAttachVmInput {
  /** Товар: нужно только название (структурно, без импорта из окна «Общий товар» — между фичами нет цикла). */
  product: QueryResultLike<{ name: string }>
  newUrl: string | null
  frameLost: boolean
  upload: PhotoUploadVm | null
  canWrite: boolean
}

export function buildPhotoAttachVm(input: PhotoAttachVmInput): PhotoAttachVm {
  const productView = queryView(input.product, () => false)
  const loaded = productView.status === 'ready'
  const blocker = !input.canWrite
    ? 'Прикреплять фото может тот, кому выдано право менять общий каталог.'
    : input.frameLost || input.newUrl === null
      ? 'Снимка нет: страницу обновили. Вернитесь и снимите товар ещё раз.'
      : !loaded
        ? 'Подождите: читаю товар.'
        : input.upload !== null
          ? input.upload.phase === 'failed'
            ? 'Загрузка не удалась: нажмите «Повторить» или «Закрыть».'
            : input.upload.phase === 'review'
              ? 'Проверьте обрезку фото ниже: «Принять» сохранит его, «Отмена» не загрузит.'
              : 'Идёт загрузка — дождитесь конца или отмените её.'
          : null
  return {
    product: productView,
    productName: input.product.data?.name ?? null,
    newUrl: input.newUrl,
    frameLost: input.frameLost,
    upload: input.upload,
    canWrite: input.canWrite,
    blocker,
  }
}
